import { Button, MarkdownEditor, Switch, Tooltip } from "@octopusdeploy/design-system-components";
import { NameSummaryWithSlug, SlugEditor, TextWithLabel as Text } from "@octopusdeploy/design-system-octopus-components";
import { type FeatureToggleEnvironmentResource, type FeatureToggleResource, Permission, type ProjectResource, Repository, type EnvironmentV2Resource, type TenantResource, type EnvironmentType, type TenantedDeploymentMode, } from "@octopusdeploy/octopus-server-client";
import type { AnalyticsEvent } from "@octopusdeploy/portal-analytics";
import { useTrackEvent } from "@octopusdeploy/portal-analytics";
import { links } from "@octopusdeploy/portal-routes";
import { isEqual } from "lodash";
import type * as React from "react";
import { useMemo, useState } from "react";
import ToggleScope from "~/areas/projects/components/FeatureToggles/FeatureToggleEnvironment";
import type { ProjectContextProps } from "~/areas/projects/context/index";
import { repository } from "~/clientInstance";
import Markdown from "~/components/Markdown/Markdown";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenuConverterVNext } from "~/components/OverflowMenu/OverflowMenuConverterVNext";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { RemoveItemsList } from "~/components/RemoveItemsList/RemoveItemsList";
import { Form, useForm } from "~/components/form/Form/Form";
import { ExpandableFormSection, FormSection, required, Summary } from "~/components/form/index";
import type { TagIndex } from "~/components/tagSets";
import { getTagIndexForTagSets } from "~/components/tagSets";
import { isTooltipHoverable } from "~/utils/TooltipHelper/isTooltipHoverable";
import { useWorkflow } from "~/workflows/useWorkflow";
import { workflows } from "~/workflows/workflows";
export interface FeatureTogglePageData {
    project: ProjectResource;
    toggle: FeatureToggleResource;
    allEnvironments: EnvironmentV2Resource[];
    tenants: TenantResource[];
    tagIndex: TagIndex;
}
export async function featureTogglePageLoader(repository: Repository, slug: string, projectContext: Promise<ProjectContextProps>): Promise<FeatureTogglePageData> {
    const { state: { model: project }, } = await projectContext;
    const environmentTypes: EnvironmentType[] = ["Static", "Ephemeral"];
    const allEnvironments = repository.Environments.getV2({ spaceId: project.SpaceId, skip: 0, take: Repository.takeAll, type: environmentTypes });
    const tenants = repository.Tenants.all({ projectId: project.Id });
    const toggle = repository.FeatureToggles.get(project, slug);
    const tenantTagSetsPromise = repository.TagSets.getAllByScopes(["Tenant"]);
    return {
        allEnvironments: (await allEnvironments).Items,
        tenants: await tenants,
        tagIndex: getTagIndexForTagSets(await tenantTagSetsPromise),
        toggle: await toggle,
        project,
    };
}
export interface FeatureTogglePageProps {
    data: FeatureTogglePageData;
}
interface EditFeatureToggleEvent extends AnalyticsEvent<string> {
    "Environment Added": boolean;
    "Environment Deleted": boolean;
    Enabled: boolean;
    Disabled: boolean;
    "Included Tenants": boolean;
    "Excluded Tenants": boolean;
    "Percentage Tenants": boolean;
    Segments: boolean;
    "Minimum Version": boolean;
    "Default Value": boolean;
}
class ToggleEnvironmentList extends RemoveItemsList<FeatureToggleEnvironmentResource> {
}
export function FeatureTogglePage({ data }: FeatureTogglePageProps) {
    const { tenants, tagIndex, allEnvironments, project, toggle: initialToggle } = data;
    const { navigate } = useSpaceAwareNavigation();
    const trackEvent = useTrackEvent();
    const [cleanModel, setCleanModel] = useState<FeatureToggleResource>(initialToggle);
    const [canDeleteAndEditSlug] = useState(isAllowed({ permission: Permission.FeatureToggleEdit, project: project.Id, environment: "*" }) &&
        cleanModel.Environments.every((e) => isAllowed({ permission: Permission.FeatureToggleEdit, project: data.project.Id, environment: e.DeploymentEnvironmentId })));
    const { model: toggle, setModel: setToggle, formProps, createSaveAction, } = useForm({
        formName: "Feature toggle",
        initialModel: initialToggle,
        submitPermission: {
            permission: Permission.FeatureToggleEdit,
            project: project.Id,
            environment: "*",
        },
        onSubmit: async (repository, model) => {
            const result = await repository.FeatureToggles.modify(data.project, model);
            trackEvent(buildFeatureToggleEditEvent(cleanModel, model));
            setCleanModel(result);
            return result;
        },
        updateFormModelAfterSubmit: (model) => model,
        afterSubmit: async (_, updatedToggle) => {
            if (toggle.Slug !== updatedToggle.Slug) {
                navigate(links.featureTogglePage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, featureToggleSlug: updatedToggle.Slug }));
            }
        },
    });
    const overflowMenu = useMemo(() => {
        async function deleteToggle(toDelete: FeatureToggleResource): Promise<boolean> {
            if (!toDelete)
                return false;
            await repository.FeatureToggles.delete(project, toDelete.Id);
            trackEvent({ eventName: "Delete Feature Toggle" });
            navigate(links.featureTogglesPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug }));
            return true;
        }
        function navigateToAuditLog(featureToggle: FeatureToggleResource) {
            const auditUrl = links.auditPage.generateUrl({ regardingAny: [featureToggle.Id] });
            navigate(auditUrl);
        }
        const auditLogItem = OverflowMenuItems.item("View Audit Log", () => navigateToAuditLog(toggle));
        const deleteItem = canDeleteAndEditSlug
            ? OverflowMenuItems.deleteItemDefault("feature toggle", () => deleteToggle(toggle))
            : OverflowMenuItems.disabledItem("Delete", "You need permission to edit the toggle and all configured environments to delete");
        return OverflowMenuConverterVNext.convertAll([auditLogItem, deleteItem]);
    }, [project, toggle, navigate, trackEvent, canDeleteAndEditSlug]);
    return (<PageContent header={{
            title: toggle.Name,
            overflowActions: overflowMenu.menuItems,
            breadcrumbs: [{ label: "Feature Toggles", pageUrl: links.featureTogglesPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug }) }],
            primaryAction: createSaveAction(),
        }}>
            {overflowMenu.dialogs}
            <Form {...formProps}>
                <EditFeatureToggle allEnvironments={allEnvironments} tenants={tenants} tagIndex={tagIndex} model={toggle} setModel={setToggle} tenantedDeploymentMode={project.TenantedDeploymentMode} canDeleteAndEditSlug={canDeleteAndEditSlug}/>
            </Form>
        </PageContent>);
}
interface EditFeatureToggleProps {
    allEnvironments: EnvironmentV2Resource[];
    tenants: TenantResource[];
    tagIndex: TagIndex;
    model: FeatureToggleResource;
    setModel: (value: React.SetStateAction<FeatureToggleResource>) => void;
    tenantedDeploymentMode: TenantedDeploymentMode;
    canDeleteAndEditSlug: boolean;
}
function EditFeatureToggle({ allEnvironments, tenants, tagIndex, model, setModel, tenantedDeploymentMode, canDeleteAndEditSlug }: EditFeatureToggleProps) {
    const [originalSlug] = useState(model.Slug);
    const addFeatureToggleEnvironmentWorkflow = useWorkflow(workflows.addFeatureToggleEnvironmentWorkflow);
    const editFeatureToggleEnvironmentWorkflow = useWorkflow(workflows.editFeatureToggleEnvironmentWorkflow);
    function nameSummary() {
        if (!model.Name)
            return Summary.placeholder("Please enter a name for the feature");
        return Summary.summary(<NameSummaryWithSlug name={model.Name} slug={model.Slug}/>);
    }
    function noteSummary() {
        if (!model.Description)
            return Summary.placeholder("Enter markdown notes");
        return Summary.summary(<Markdown markup={model.Description}/>);
    }
    function defaultValueSummary() {
        return Summary.summary(model.DefaultIsEnabled ? "On" : "Off");
    }
    function environmentRow(toggle: FeatureToggleEnvironmentResource, tagIndex: TagIndex, idx: number) {
        const rowTenants = tenants.filter((t) => toggle.TenantIds.indexOf(t.Id) !== -1 || toggle.ExcludedTenantIds.indexOf(t.Id) !== -1);
        const handleClick = (e: React.MouseEvent<HTMLSpanElement>) => e.stopPropagation();
        const isAllowedToEdit = isAllowedToEditToggleEnvironment(toggle.DeploymentEnvironmentId);
        return (<div>
                <span onClick={handleClick}>
                    <Tooltip open={isTooltipHoverable(!isAllowedToEdit)} content="You don't have permission to configure feature toggles for this environment">
                        <Switch value={toggle.IsEnabled} onChange={(value) => setToggleEnvironment(value, idx)} disabled={!isAllowedToEdit}/>
                    </Tooltip>
                </span>
                <ToggleScope featureToggleEnvironment={toggle} environments={allEnvironments} tenants={rowTenants} tagIndex={tagIndex}/>
                {toggle.MinimumVersion && (<>
                        <code>&gt;= {toggle.MinimumVersion}</code>
                    </>)}
            </div>);
    }
    function setToggleEnvironment(value: boolean, idx: number) {
        const toggleEnvironments = [...model.Environments];
        toggleEnvironments[idx] = { ...toggleEnvironments[idx], IsEnabled: value };
        setModel({ ...model, Environments: toggleEnvironments });
    }
    async function addToggleEnvironment(environment: FeatureToggleEnvironmentResource) {
        setModel({ ...model, Environments: [...model.Environments, environment] });
        return true;
    }
    async function editToggleEnvironment(environment: FeatureToggleEnvironmentResource, index: number) {
        const toggles = [...model.Environments];
        toggles[index] = environment;
        setModel({ ...model, Environments: toggles });
        return true;
    }
    async function removeToggleEnvironment(toggle: FeatureToggleEnvironmentResource) {
        setModel({
            ...model,
            Environments: model.Environments.filter((t) => t !== toggle),
        });
    }
    function isAllowedToEditToggleEnvironment(environmentId: string) {
        return isAllowed({ permission: Permission.FeatureToggleEdit, environment: environmentId, project: model.ProjectId });
    }
    return (<div>
            <ExpandableFormSection errorKey="Name" title="Name" summary={nameSummary()}>
                <Text value={model.Name} label={"Feature Name"} validate={required("Please provide a name for the feature")} onChange={(value) => {
            setModel({ ...model, Name: value });
        }}/>
                <SlugEditor label="Slug" value={model.Slug} originalSlug={originalSlug} readOnly={!canDeleteAndEditSlug} readOnlyMessage={"You need permission to edit the toggle and all configured environments to change this slug"} onChange={(value) => {
            setModel({ ...model, Slug: value });
        }} additionalWarningMessage={"Modifying a slug may impact feature toggle evaluations for software referencing the slug. Any references to the old slug will have to be manually updated, and careful consideration should be given to managing the changeover from the old slug to the new slug."} validate={required("Please enter a feature toggle slug")}/>
            </ExpandableFormSection>
            <ExpandableFormSection errorKey="Description" title="Notes" summary={noteSummary()}>
                <MarkdownEditor label="Notes" value={model.Description} onChange={(value) => {
            setModel({ ...model, Description: value });
        }}/>
            </ExpandableFormSection>
            <ExpandableFormSection title="Default Value" errorKey="DefaultValue" summary={defaultValueSummary()} help={"The default value will be returned for any environments not configured below"}>
                <Switch value={model.DefaultIsEnabled} onChange={(value) => {
            setModel({ ...model, DefaultIsEnabled: value });
        }}/>
            </ExpandableFormSection>
            <FormSection title="Environments" includeBorder={true}>
                <Button importance={"secondary"} label="Add Environment" onClick={() => addFeatureToggleEnvironmentWorkflow({ spaceId: model.SpaceId, projectId: model.ProjectId, tenantedDeploymentMode: tenantedDeploymentMode }, addToggleEnvironment)}/>
                <ToggleEnvironmentList data={model.Environments} onRow={(toggle, idx) => environmentRow(toggle, tagIndex, idx)} onRowTouch={(featureToggleEnvironment) => {
            // Passing current index through to the save function to be sure that it is up to date
            const index = model.Environments.indexOf(featureToggleEnvironment);
            editFeatureToggleEnvironmentWorkflow({ spaceId: model.SpaceId, projectId: model.ProjectId, featureToggleEnvironment, tenantedDeploymentMode: tenantedDeploymentMode }, (updatedFeatureToggleEnv) => editToggleEnvironment(updatedFeatureToggleEnv, index));
        }} onRemoveRow={removeToggleEnvironment} removeButtonDisabled={{
            isDisabled: (item) => !isAllowedToEditToggleEnvironment(item.DeploymentEnvironmentId),
            disabledButtonToolTip: "You don't have permission to configure feature toggles for this environment",
        }}/>
            </FormSection>
        </div>);
}
export function buildFeatureToggleEditEvent(cleanModel: FeatureToggleResource, model: FeatureToggleResource): EditFeatureToggleEvent {
    const ev: EditFeatureToggleEvent = {
        eventName: "Edit Feature Toggle",
        "Environment Added": false,
        "Environment Deleted": false,
        Enabled: false,
        Disabled: false,
        "Included Tenants": false,
        "Excluded Tenants": false,
        "Percentage Tenants": false,
        Segments: false,
        "Minimum Version": false,
        "Default Value": false,
    };
    // Check if default value changed
    if (cleanModel.DefaultIsEnabled !== model.DefaultIsEnabled) {
        ev["Default Value"] = true;
    }
    const oldEnvironments = cleanModel.Environments;
    const updatedEnvironments = model.Environments;
    // Work out if any environments have been added or deleted
    const oldEnvironmentIds = new Set(oldEnvironments.map((e) => e.DeploymentEnvironmentId));
    const updatedEnvironmentIds = new Set(updatedEnvironments.map((e) => e.DeploymentEnvironmentId));
    const addedEnvironmentIds = [...updatedEnvironmentIds].filter((id) => !oldEnvironmentIds.has(id));
    if (addedEnvironmentIds.length > 0) {
        ev["Environment Added"] = true;
    }
    const deletedEnvironmentIds = [...oldEnvironmentIds].filter((id) => !updatedEnvironmentIds.has(id));
    if (deletedEnvironmentIds.length > 0) {
        ev["Environment Deleted"] = true;
    }
    // For environments that have been added or modified, check what changed
    for (const newEnv of updatedEnvironments) {
        const oldEnv = oldEnvironments.find((e) => e.DeploymentEnvironmentId === newEnv.DeploymentEnvironmentId);
        if (!oldEnv) {
            if (newEnv.IsEnabled) {
                ev["Enabled"] = true;
            }
            // We don't set the disabled property for new environments because that's
            // the default.
            if (newEnv.TenantIds.length > 0) {
                ev["Included Tenants"] = true;
            }
            if (newEnv.ExcludedTenantIds.length > 0) {
                ev["Excluded Tenants"] = true;
            }
            if (newEnv.RolloutPercentage !== null && newEnv.RolloutPercentage !== 0) {
                ev["Percentage Tenants"] = true;
            }
            if (newEnv.Segments.length > 0) {
                ev["Segments"] = true;
            }
            if (newEnv.MinimumVersion !== null) {
                ev["Minimum Version"] = true;
            }
        }
        else {
            // Existing environment modified
            if (!oldEnv.IsEnabled && newEnv.IsEnabled) {
                ev["Enabled"] = true;
            }
            if (oldEnv.IsEnabled && !newEnv.IsEnabled) {
                ev["Disabled"] = true;
            }
            if (!isEqual([...oldEnv.TenantIds].sort(), [...newEnv.TenantIds].sort())) {
                ev["Included Tenants"] = true;
            }
            if (!isEqual([...oldEnv.ExcludedTenantIds].sort(), [...newEnv.ExcludedTenantIds].sort())) {
                ev["Excluded Tenants"] = true;
            }
            if (oldEnv.RolloutPercentage !== newEnv.RolloutPercentage) {
                ev["Percentage Tenants"] = true;
            }
            if (!isEqual(oldEnv.Segments, newEnv.Segments)) {
                ev["Segments"] = true;
            }
            if (oldEnv.MinimumVersion !== newEnv.MinimumVersion) {
                ev["Minimum Version"] = true;
            }
        }
    }
    return ev;
}
